import React, { useEffect, useState } from 'react';
import axios from '../../../utils/api/axios';
import { useParams, useLocation } from 'react-router-dom';
import { UserRole } from '../../../utils/api/endpoints/user/credentials';
import toast from 'react-hot-toast';
import { MdAdd } from 'react-icons/md';
import AddEndpointModal from './AddEndpointModal';

interface Resource {
    resource_id: number;
    target_id: string;
    value: string;
    resource_type: string;
    status: string;
    discovery_method: string;
    notes: string;
    http_method?: string | null;
    created_at: string;
    creator?: {
        display_name: string;
        email: string;
        username?: string;
    };
}

interface DiscoveredEndpointsTableProps {
    role: UserRole;
    assetId?: string;
}

const DiscoveredEndpointsTable: React.FC<DiscoveredEndpointsTableProps> = ({ role, assetId }) => {
    const { id } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const highlightedResourceId = queryParams.get("resourceId");

    const [resources, setResources] = useState<Resource[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddEndpointModalOpen, setIsAddEndpointModalOpen] = useState(false);
    const [selectedTargetId, setSelectedTargetId] = useState<string>('');
    const [availableTargets, setAvailableTargets] = useState<any[]>([]);

    useEffect(() => {
        if (highlightedResourceId && resources.length > 0) {
            const timer = setTimeout(() => {
                const element = document.getElementById(`resource-${highlightedResourceId}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [highlightedResourceId, resources]);

    const fetchDiscoveries = React.useCallback(async () => {
        try {
            setIsLoading(true);
            // We need to fetch resources for all targets in the program
            // For now, let's assume we have an endpoint that returns all discoveries for a program
            // Since we don't have one yet, we might need to fetch program targets first
            // or use a new combined endpoint.
            // Let's use the hierarchical scope endpoint we just created!
            const response = await axios.get(`/v2/programs/${id}/scope-hierarchical`);
            if (response.data?.data?.targets) {
                const allResources: Resource[] = [];
                const targets = response.data.data.targets;
                setAvailableTargets(targets);

                targets.forEach((target: any) => {
                    if (target.resources) {
                        // Filter for PENDING or those with discovery_method = PENTESTER
                        const discoveries = target.resources.filter((r: Resource) =>
                            r.discovery_method === 'PENTESTER' || r.status === 'PENDING'
                        );
                        // Add target information to each discovery
                        discoveries.forEach((d: Resource) => {
                            allResources.push({
                                ...d,
                                target_name: target.target_name,
                                target_value: target.target_value,
                                target_type: target.target_type
                            } as any);
                        });
                    }
                });
                setResources(allResources.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
            }
        } catch (error) {
            console.error("Error fetching discoveries:", error);
            toast.error("Failed to load discoveries");
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) fetchDiscoveries();
    }, [id, fetchDiscoveries]);

    const isResearcher = role === UserRole.RESEARCHER;

    if (isLoading) return <div className="p-4 text-center">Loading discoveries...</div>;

    if (resources.length === 0) {
        return (
            <div className="space-y-4">
                {/* Add Endpoint Button - Only for Researchers */}
                {isResearcher && (
                    <div className="flex justify-end">
                        <button
                            onClick={() => {
                                if (availableTargets.length > 0) {
                                    setIsAddEndpointModalOpen(true);
                                } else {
                                    toast.error("No targets available in this program");
                                }
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                        >
                            <MdAdd className="w-5 h-5" />
                            Discover Endpoint
                        </button>
                    </div>
                )}

                <div className="bg-white rounded-lg border p-8 text-center">
                    <p className="text-gray-500">No discovered endpoints found for this program.</p>
                    {isResearcher && (
                        <p className="text-sm text-gray-400 mt-2">Click "Discover Endpoint" to add your first discovery.</p>
                    )}
                </div>

                {/* Add Endpoint Modal */}
                {assetId && (
                    <AddEndpointModal
                        isOpen={isAddEndpointModalOpen}
                        onClose={() => {
                            setIsAddEndpointModalOpen(false);
                        }}
                        onSuccess={fetchDiscoveries}
                        programId={parseInt(id!)}
                        assetId={assetId}
                        availableTargets={availableTargets.map(t => ({
                            target_id: t.target_id,
                            target_name: t.target_name,
                            target_value: t.target_value
                        }))}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header with Add Button */}
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">Discovered Endpoints</h3>
                {isResearcher && (
                    <button
                        onClick={() => {
                            if (availableTargets.length > 0) {
                                setIsAddEndpointModalOpen(true);
                            } else {
                                toast.error("No targets available in this program");
                            }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                    >
                        <MdAdd className="w-5 h-5" />
                        Discover Endpoint
                    </button>
                )}
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow border overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Researcher</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {resources.map((resource: any) => {
                            const isHighlighted = resource.resource_id.toString() === highlightedResourceId;
                            return (
                                <tr 
                                    key={resource.resource_id}
                                    id={`resource-${resource.resource_id}`}
                                    className={isHighlighted ? "bg-[#58CC02]/10 transition-colors duration-500" : "hover:bg-gray-50"}
                                >
                                    <td className={`px-6 py-4 text-sm text-gray-900 ${isHighlighted ? "border-l-4 border-[#58CC02]" : ""}`}>
                                    <div className="font-medium">{resource.target_name || 'N/A'}</div>
                                    <div className="text-xs text-gray-500 truncate max-w-xs" title={resource.target_value}>
                                        {resource.target_value || ''}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">{resource.http_method || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{resource.value}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{resource.resource_type}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {resource.creator?.username || resource.creator?.display_name || 'N/A'}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={resource.notes}>
                                    {resource.notes}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                        {resource.status === 'PENDING' ? 'IN_SCOPE' : resource.status}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>

            {/* Add Endpoint Modal */}
            {assetId && (
                <AddEndpointModal
                    isOpen={isAddEndpointModalOpen}
                    onClose={() => {
                        setIsAddEndpointModalOpen(false);
                    }}
                    onSuccess={fetchDiscoveries}
                    programId={parseInt(id!)}
                    assetId={assetId}
                    availableTargets={availableTargets.map(t => ({
                        target_id: t.target_id,
                        target_name: t.target_name,
                        target_value: t.target_value
                    }))}
                />
            )}
        </div>
    );
};

export default DiscoveredEndpointsTable;
